// @ts-check
import { Activity, CreditCard, Eye, Globe, Heart, Radio, ShoppingBag, ShoppingCart, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import api from '../utils/apiClient';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Country code → approximate lat/lng center (ISO 3166-1 alpha-2)
const COUNTRY_COORDS = {
  US: [-95.7, 37.1],
  IN: [78.9, 20.6],
  GB: [-1.2, 52.3],
  UK: [-1.2, 52.3],
  AE: [54.0, 24.0],
  AU: [133.8, -25.3],
  CA: [-106.3, 56.1],
  DE: [10.4, 51.2],
  FR: [2.2, 46.2],
  IT: [12.6, 41.9],
  JP: [138.3, 36.2],
  CN: [104.2, 35.9],
  BR: [-51.9, -14.2],
  MX: [-102.6, 23.6],
  KR: [127.8, 35.9],
  ES: [-3.7, 40.5],
  NL: [5.3, 52.1],
  SE: [18.6, 60.1],
  NO: [8.5, 60.5],
  CH: [8.2, 46.8],
  SA: [45.1, 23.9],
  SG: [103.8, 1.4],
  MY: [101.7, 4.2],
  TH: [100.5, 15.9],
  PH: [121.8, 12.9],
  ID: [113.9, -0.8],
  ZA: [22.9, -30.6],
  NG: [8.7, 9.1],
  KE: [37.9, -0.02],
  EG: [30.8, 26.8],
  PK: [69.3, 30.4],
  BD: [90.4, 23.7],
  LK: [80.8, 7.9],
  NP: [84.1, 28.4],
  QA: [51.2, 25.4],
  KW: [47.5, 29.3],
  OM: [55.9, 21.5],
  BH: [50.6, 26.0],
  NZ: [174.9, -40.9],
  IE: [-8.2, 53.4],
  PT: [-8.2, 39.4],
  PL: [19.1, 51.9],
  AT: [14.6, 47.5],
  BE: [4.5, 50.5],
  DK: [9.5, 56.3],
  FI: [25.7, 61.9],
  CZ: [15.5, 49.8],
  GR: [21.8, 39.1],
  HU: [19.5, 47.2],
  RO: [24.97, 45.9],
  RU: [105.3, 61.5],
  UA: [31.2, 48.4],
  TR: [35.2, 38.9],
  IL: [34.8, 31.0],
  AR: [-63.6, -38.4],
  CL: [-71.5, -35.7],
  CO: [-74.3, 4.6],
  PE: [-75.0, -9.2],
  EU: [10.4, 51.2], // fallback for EU
};

const EVENT_CONFIG = {
  page_view: { label: 'Viewing', color: '#6366f1', icon: Eye },
  view_content: { label: 'Viewing Product', color: '#8b5cf6', icon: Eye },
  add_to_cart: { label: 'Added to Cart', color: '#22c55e', icon: ShoppingCart },
  remove_from_cart: { label: 'Removed from Cart', color: '#ef4444', icon: ShoppingCart },
  wishlist_add: { label: 'Added to Wishlist', color: '#ec4899', icon: Heart },
  initiate_checkout: { label: 'Checkout Started', color: '#f59e0b', icon: CreditCard },
  order_completed: { label: 'Order Placed', color: '#10b981', icon: ShoppingBag },
  search: { label: 'Searching', color: '#3b82f6', icon: Activity },
  newsletter_signup: { label: 'Newsletter Signup', color: '#06b6d4', icon: UserPlus },
};

const getEventConfig = (type) => EVENT_CONFIG[type] || { label: type, color: '#6b7280', icon: Activity };

const LiveMapPage = () => {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ active_sessions: 0, events_today: 0, active_carts: 0, orders_today: 0 });
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const eventIdRef = useRef(0);

  // Load recent events for initial map population
  useEffect(() => {
    api
      .get('/api/analytics/recent?limit=30')
      .then((data) => {
        const mapped = data
          .map((e, i) => ({
            ...e,
            _id: `init_${i}`,
            _coords: COUNTRY_COORDS[e.ip_country] || null,
            _age: 'old',
          }))
          .filter((e) => e._coords);
        setEvents(mapped);
      })
      .catch(() => {});
  }, []);

  // Compute live stats from current events
  useEffect(() => {
    const sessions = new Set(events.map((e) => e.session_id).filter(Boolean));
    const carts = events.filter((e) => e.event_type === 'add_to_cart');
    const orders = events.filter((e) => e.event_type === 'order_completed');
    setStats({
      active_sessions: sessions.size,
      events_today: events.length,
      active_carts: carts.length,
      orders_today: orders.length,
    });
  }, [events]);

  // WebSocket connection
  const connectWs = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/^https?:/, wsProtocol);
    const wsUrl = `${backendUrl}/ws/live-map${token ? `?token=${token}` : ''}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      clearTimeout(reconnectRef.current);
    };

    ws.onmessage = (msg) => {
      try {
        const event = JSON.parse(msg.data);
        const coords = COUNTRY_COORDS[event.ip_country];
        if (!coords) return; // skip events without known coords

        const id = `live_${eventIdRef.current++}`;
        const newEvent = { ...event, _id: id, _coords: coords, _age: 'new' };

        setEvents((prev) => {
          const updated = [newEvent, ...prev];
          // Keep max 100 events on map
          return updated.slice(0, 100);
        });

        // Fade to old after 10 seconds
        setTimeout(() => {
          setEvents((prev) => prev.map((e) => (e._id === id ? { ...e, _age: 'old' } : e)));
        }, 10000);

        // Remove after 2 minutes
        setTimeout(() => {
          setEvents((prev) => prev.filter((e) => e._id !== id));
        }, 120000);
      } catch (e) {
        console.debug('WS parse error:', e);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      // Reconnect after 3 seconds
      reconnectRef.current = setTimeout(connectWs, 3000);
    };

    ws.onerror = () => ws.close();
  }, []);

  useEffect(() => {
    connectWs();
    return () => {
      clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connectWs]);

  // Send periodic pings to keep connection alive
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="-m-4 lg:-m-6 h-[calc(100dvh-64px)] flex flex-col bg-gray-950 text-white overflow-hidden">
      {/* Stats Bar */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 lg:px-6 py-3 bg-gray-900 border-b border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Globe className="w-5 h-5 text-blue-400" />
          <h1 className="text-lg font-bold">Live Map</h1>
          <span
            className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${
              connected ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'
            }`}
          >
            <Radio className="w-3 h-3" />
            {connected ? 'Connected' : 'Reconnecting...'}
          </span>
        </div>
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="text-center">
            <div className="text-xl font-bold text-white">{stats.active_sessions}</div>
            <div className="text-[10px] uppercase text-muted-foreground/70 tracking-wider">Active</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-white">{stats.events_today}</div>
            <div className="text-[10px] uppercase text-muted-foreground/70 tracking-wider">Events</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-green-400">{stats.active_carts}</div>
            <div className="text-[10px] uppercase text-muted-foreground/70 tracking-wider">Carts</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-emerald-400">{stats.orders_today}</div>
            <div className="text-[10px] uppercase text-muted-foreground/70 tracking-wider">Orders</div>
          </div>
        </div>
      </div>

      {/* Map + Event Feed */}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
        {/* Map */}
        <div className="flex-1 min-h-0 relative">
          <div className="absolute inset-0">
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 140, center: [20, 20] }}
              style={{ width: '100%', height: '100%', background: '#0a0a1a' }}
            >
              <ZoomableGroup>
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rpiKey || geo.id}
                        geography={geo}
                        fill="#1e293b"
                        stroke="#334155"
                        strokeWidth={0.5}
                        style={{
                          default: { outline: 'none' },
                          hover: { fill: '#334155', outline: 'none' },
                          pressed: { outline: 'none' },
                        }}
                      />
                    ))
                  }
                </Geographies>

                {/* Event markers */}
                {events.map((event) => {
                  const config = getEventConfig(event.event_type);
                  const isNew = event._age === 'new';
                  return (
                    <Marker key={event._id} coordinates={event._coords}>
                      {/* Pulse ring for new events */}
                      {isNew && (
                        <circle r={12} fill="none" stroke={config.color} strokeWidth={2} opacity={0.6}>
                          <animate attributeName="r" from="6" to="20" dur="1.5s" repeatCount="3" />
                          <animate attributeName="opacity" from="0.8" to="0" dur="1.5s" repeatCount="3" />
                        </circle>
                      )}
                      {/* Dot */}
                      <circle
                        r={isNew ? 5 : 3}
                        fill={config.color}
                        opacity={isNew ? 1 : 0.5}
                        style={{ transition: 'all 0.5s ease' }}
                      />
                    </Marker>
                  );
                })}
              </ZoomableGroup>
            </ComposableMap>
          </div>
        </div>

        {/* Event Feed Sidebar */}
        <div className="w-full md:w-80 h-56 md:h-auto flex-shrink-0 bg-gray-900 border-t md:border-t-0 md:border-l border-gray-800 flex flex-col">
          <div className="px-4 py-3 border-b border-gray-800">
            <h2 className="text-sm font-bold text-muted-foreground/50 uppercase tracking-wider">Live Activity</h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {events.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Activity className="w-8 h-8 mb-2" />
                <p className="text-sm">Waiting for events...</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {events.slice(0, 30).map((event) => {
                  const config = getEventConfig(event.event_type);
                  const Icon = config.icon;
                  const isNew = event._age === 'new';
                  const productName = event.metadata?.product_name || '';
                  const time = event.created_at
                    ? new Date(event.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                    : '';
                  return (
                    <div
                      key={event._id}
                      className={`px-4 py-3 transition-all duration-500 ${isNew ? 'bg-gray-800/50' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ backgroundColor: `${config.color}20` }}
                        >
                          <Icon className="w-3.5 h-3.5" style={{ color: config.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-white">{config.label}</span>
                            {isNew && <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />}
                          </div>
                          {productName && (
                            <p className="text-xs text-muted-foreground/70 truncate mt-0.5">{productName}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-muted-foreground">{event.ip_country || 'Unknown'}</span>
                            <span className="text-[10px] text-muted-foreground">{time}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveMapPage;
